import React, { useState, useEffect, useRef } from "react";
import { FaCaretDown } from "react-icons/fa";
import {
  RadioInput,
  TextInput,
  DateInput,
  NumberInput,
  DateTimeInput,
  NewSelectInput,
  NewNativeSelectInput,
  TextArea,
} from "../ui/Input";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { BiSolidError } from "react-icons/bi";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import { Spinner } from "../ui/Skeleton";

function ArticleForm({
  text,
  onSave,
  onCancel,
  initialData,
  productionHandlers,
  fieldConfiguration,
  onback,
}) {
  const [formData, setFormData] = useState(initialData || {});
  const [initialFormData, setInitialFormData] = useState(initialData || {});
  const [fieldConfig, setFieldConfig] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  let [popover, setPopover] = useState(false);

  const formRef = useRef(null);

  useEffect(() => {
    const formElement = formRef.current;

    const handleScroll = () => {
      if (popover) {
        setPopover(false);
      }
    };

    if (formElement) {
      formElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (formElement) {
        formElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, [popover]);

  // Process field configuration into a lookup object
  useEffect(() => {
    if (fieldConfiguration) {
      const configObj = fieldConfiguration.reduce((acc, field) => {
        acc[field.key] = field;
        return acc;
      }, {});
      setFieldConfig(configObj);
    }
    setInitialFormData(initialData || {});
  }, [fieldConfiguration, initialData]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let parsedValue;

    if (type === "checkbox" || type === "radio") {
      parsedValue = checked;
    } else if (type === "select-one") {
      // Convert string to actual boolean or null
      if (value === "true") parsedValue = true;
      else if (value === "false") parsedValue = false;
      else if (value === "Selected" && name === "coverSubReceived")
        parsedValue = null;
      else parsedValue = value;
    } else {
      parsedValue = value;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: parsedValue,
    }));
  };

  const normalizeValue = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return true;
      if (value.toLowerCase() === "false") return false;
      return value.trim();
    }
    return value;
  };

  const checkChanges = () => {
    for (const key in initialFormData) {
      const prev = normalizeValue(initialFormData[key]);
      const current = normalizeValue(formData[key]);

      if (prev !== current) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    let value = checkChanges();
    setHasUnsavedChanges(value);
  }, [formData]);

  const handleSave = async () => {
    try {
      if (hasUnsavedChanges) {
        setFormSubmitting(true);

        const sanitizedFormData = Object.fromEntries(
          Object.entries(formData).map(([key, value]) => [
            key,
            typeof value === "string" ? value.trim() : value,
          ])
        );

        await onSave(sanitizedFormData);
      }
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setFormSubmitting(false);
    }
  };

  let hasChangesArray = [
    "issue",
    "volume",
    // "productionWorkflow",
    // "handlingEditor",
    // "dateBackFromSce",
    // "sceReturnedStatus",
  ];
  const getPreviousValue = (fieldName) => {
  if (!initialData?.changes || !Array.isArray(initialData.changes))
    return null;

  const changeEntry = initialData.changes.find(
    (change) => change.key === fieldName && change.previousValue != null
  );

  if (changeEntry && hasChangesArray.includes(fieldName)) {
    const prev =
      changeEntry.previousValue === false
        ? "No"
        : changeEntry.previousValue === true
        ? "Yes"
        : changeEntry.previousValue;


    if (changeEntry.isManual === false) {
      return `Content Replace. Replaced Content: [${prev}].`;
    }

    // isManual true / undefined → mismatch
    return `Content Mismatch. Mismatched Content: [${prev}].`;
  }

  return null;
};

  // Get previous value from change history
  // const getPreviousValue = (fieldName) => {
  //   if (!initialData?.changes || !Array.isArray(initialData.changes))
  //     return null;
  //   const changeEntry = initialData.changes.find(
  //     (change) => change.key === fieldName && change.previousValue != null
  //   );
  //   if (changeEntry && hasChangesArray.includes(fieldName)) {
  //     return `Content Mismatch. Mismatched Content: [${
  //       changeEntry.previousValue === false
  //         ? "No"
  //         : changeEntry.previousValue === true
  //         ? "Yes"
  //         : changeEntry.previousValue
  //     }].`;
  //   }

  
    //  else if (changeEntry && !hasChangesArray.includes(fieldName)) {
    //   return `Content replaced. Previous Content: [${
    //     changeEntry.previousValue === false
    //       ? "No"
    //       : changeEntry.previousValue === true
    //       ? "Yes"
    //       : changeEntry.previousValue
    //   }].`;
    // }
    // else {
    //   return null;
    // }
//  };

  // Check for changes
  const hasChanges =
    initialData?.changes &&
    Array.isArray(initialData.changes) &&
    initialData.changes.some(
      (change) =>
        change.key &&
        hasChangesArray.includes(change.key) &&
        change.previousValue != null
    );

  const handleNavigate = () => {
    if (text === "My Task (Article)") {
      onCancel();
    } else {
      onback();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white py-1 px-2">
        <div className="flex items-center space-x-1 mb-2">
          <button onClick={handleNavigate} className="flex items-center">
            <span className="text-[#868E94] hover:underline text-[13px] font-medium leading-[16px]">
              {text}
            </span>
          </button>

          {text === "Journal Overview" ? (
            <>
              <MdOutlineKeyboardArrowRight className="text-[#5D6871] text-[16px] flex-shrink-0" />
              <button onClick={onCancel} className="flex items-center">
                <span className="text-[#868E94] hover:underline text-[13px] font-medium leading-[16px]">
                  Article Overview
                </span>
              </button>
            </>
          ) : (
            <>
              <MdOutlineKeyboardArrowRight className="text-[#5D6871] text-[16px] flex-shrink-0" />
              <span className="text-[#868E94] text-[13px] font-medium leading-[16px]">
                {formData?.journalName || formData?.journalInfo?.journalName}
              </span>
            </>
          )}

          <MdOutlineKeyboardArrowRight className="text-[#5D6871] text-[16px] flex-shrink-0" />
          <span className="text-[#5D6871] text-[13px] font-medium leading-[16px]">
            {text === "Journal Overview"
              ? formData?.journalName || formData?.journalInfo?.journalName
              : formData?.pts}
          </span>
        </div>

        <div className="flex items-center">
          <button
            onClick={onCancel}
            className="text-[#20282E] text-[23px] h-[32px] font-semibold mr-2"
            type="button"
          >
            <i className="fas fa-arrow-left text-[#20282E] text-[23px] h-[32px] font-semibold"></i>
          </button>
          <h2 className="text-[23px] h-[32px] font-semibold text-[#20282E]">
            {formData?.journalName ||
              formData?.journalInfo?.journalName ||
              "No Journal"}{" "}
            {formData.pts || "N/A"}
          </h2>
        </div>
        {hasChanges && (
          <div
            className="flex items-center bg-[#FFF6F0] h-[40px] border border-[#B95000] text-[#B95000] text-[16px] font-medium px-4 py-2 rounded mt-2 mb-2"
            role="alert"
          >
            <BiSolidError className="min-w-[16px]" />
            <span className="ml-2 whitespace-nowrap overflow-hidden text-ellipsis">
              Some fields show mismatched content. Please review each to confirm
              accuracy.
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div ref={formRef} className="flex-1 overflow-y-auto">
        {formSubmitting && (
          <div className="backdrop-blur-sm z-20 fixed inset-0 ">
            <Spinner />
          </div>
        )}
        <div className="bg-white rounded-lg p-2">
          {/* Article Meta */}
          <div className="mt-2">
            <h3 className="text-lg mb-4 font-semibold text-[#5D6871]">
              Article Meta
            </h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="col-span-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <TextInput
                    label="Journal"
                    name="journalName"
                    value={
                      formData?.journalInfo?.journalName ||
                      formData?.journalName
                    }
                    onChange={handleChange}
                    previousValue={getPreviousValue("journal")}
                    disabled={true}
                  />
                </div>
                <div>
                  <TextInput
                    label="PTS#"
                    name="pts"
                    value={formData.pts}
                    onChange={handleChange}
                    disabled={true}
                    previousValue={getPreviousValue("pts")}
                  />
                </div>
                <div>
                  <TextInput
                    label="PIT"
                    name="pit"
                    value={formData.pit}
                    onChange={handleChange}
                    disabled={true}
                    previousValue={getPreviousValue("pit")}
                  />
                </div>
                <div>
                  <TextInput
                    label="Dochead"
                    name="docHead"
                    value={formData.docHead}
                    onChange={handleChange}
                    disabled={true}
                    previousValue={getPreviousValue("docHead")}
                  />
                </div>
                <div>
                  <NewSelectInput
                    label="Production handler"
                    name="productionHandler"
                    value={formData.productionHandler}
                    onChange={handleChange}
                    options={productionHandlers}
                    placeholder="Select production handler"
                    important={true}
                    popover={popover}
                    setPopover={setPopover}
                  />
                </div>
                <div>
                  {/* <TextInput
                    label="Production workflow"
                    name="productionWorkflow"
                    value={formData.productionWorkflow}
                    onChange={handleChange}
                    previousValue={getPreviousValue("productionWorkflow")}
                  /> */}
                  <NewNativeSelectInput
                    label="Production workflow"
                    name="productionWorkflow"
                    onChange={handleChange}
                    previousValue={getPreviousValue("productionWorkflow")}
                    value={formData.productionWorkflow}
                    placeholder="Select production workflow"
                    options={[
                      "IN-HOUSE",
                      "SUPPLIER COPYEDITING",
                      "ISSUE-MANAGED",
                      "SCE-PROOFS",
                    ]}
                  />
                </div>
                <div>
                  <TextInput
                    label="EM#"
                    name="em"
                    value={formData.em}
                    onChange={handleChange}
                    disabled={true}
                    previousValue={getPreviousValue("em")}
                  />
                </div>
                <div>
                  <TextInput
                    label="On completion"
                    name="completionReference"
                    value={formData.completionReference}
                    onChange={handleChange}
                    disabled={true}
                    previousValue={getPreviousValue("completionReference")}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="mt-2">
            <h3 className="text-lg font-semibold mb-4 text-[#5D6871]">Notes</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 mb-4">
              <div>
                <TextArea
                  label="PTS remarks"
                  name="ptsRemarks"
                  value={formData.ptsRemarks}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("ptsRemarks")}
                />
              </div>
              <div>
                <TextArea
                  label="Post-acceptance notes"
                  name="productionNotes"
                  value={formData.productionNotes}
                  onChange={handleChange}
                  previousValue={getPreviousValue("productionNotes")}
                />
              </div>
            </div>
          </div>

          {/* Schedule Info */}
          <div className="mt-2">
            <h3 className="text-lg font-semibold mb-4 text-[#5D6871]">
              Schedule Info
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <TextInput
                  label="Item group"
                  name="itemGroup"
                  value={formData.itemGroup}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("itemGroup")}
                />
              </div>
              <div>
                <NumberInput
                  label="Volume"
                  name="volume"
                  value={formData.volume}
                  onChange={handleChange}
                  previousValue={getPreviousValue("volume")}
                  maxValue={1000}
                />
              </div>
              <div>
                <NumberInput
                  label="Issue"
                  name="issue"
                  value={formData.issue}
                  onChange={handleChange}
                  previousValue={getPreviousValue("issue")}
                  maxValue={1000}
                />
              </div>
              <div>
                <TextInput
                  label="Vol/Iss"
                  name="volIss"
                  value={formData.volIss}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("VolIss")}
                />
              </div>
              <div>
                <DateInput
                  label="Speed target"
                  name="speedTarget"
                  value={formData.speedTarget}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("speedTarget")}
                />
              </div>
              <div>
                <DateInput
                  label="ON date"
                  name="onDate"
                  value={formData.onDate}
                  onChange={handleChange}
                  previousValue={getPreviousValue("onDate")}
                />
              </div>
              <div>
                <DateTimeInput
                  label="Embargo exp"
                  name="embargoExp"
                  value={formData.embargoExp}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("embargoExp")}
                />
              </div>
              <div>
                <TextInput
                  label="Embargo stg"
                  name="embargoStg"
                  value={formData.embargoStg}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("embargoStg")}
                />
              </div>
              <div>
                <DateInput
                  label="Article S300 date"
                  name="articleS300Date"
                  value={formData.articleS300Date}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("articleS300Date")}
                />
              </div>
              <div>
                <DateInput
                  label="Item holdout"
                  name="itemHoldout"
                  value={formData.itemHoldout}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("itemHoldout")}
                />
              </div>
              <div>
                <DateInput
                  label="Item finalizing"
                  name="itemFinalizing"
                  value={formData.itemFinalizing}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("itemFinalizing")}
                />
              </div>
            </div>
          </div>

          {/* Article Info */}
          <div className="mt-2">
            <h2 className="text-lg font-semibold mb-4 text-[#5D6871]">
              Article Info
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <TextInput
                  label="Handling editor"
                  name="handlingEditor"
                  value={formData.handlingEditor}
                  onChange={handleChange}
                  previousValue={getPreviousValue("handlingEditor")}
                />
              </div>
              <div>
                <TextInput
                  label="First author"
                  name="firstAuthor"
                  value={formData.firstAuthor}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("firstAuthor")}
                />
              </div>
              <div>
                <TextInput
                  label="Corr. author"
                  name="correspondingAuthor"
                  value={formData.correspondingAuthor}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("correspondingAuthor")}
                />
              </div>
              <div>
                <TextInput
                  label="Corr. author email"
                  name="correspondingAuthorEmail"
                  value={formData.correspondingAuthorEmail}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("correspondingAuthorEmail")}
                />
              </div>

              <div>
                <TextInput
                  label="DOI"
                  name="doi"
                  value={formData.doi}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("doi")}
                />
              </div>
              <div>
                <TextInput
                  label="PII"
                  name="pii"
                  value={formData.pii}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("pii")}
                />
              </div>
              <div>
                <TextInput
                  label="URL"
                  name="url"
                  value={formData.url}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("url")}
                />
              </div>
              <div>
                <TextInput
                  label="Title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("title")}
                />
              </div>
              <div>
                <TextInput
                  label="PTS refers to"
                  name="ptsRefersTo"
                  value={formData.ptsRefersTo}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("ptsRefersTo")}
                />
              </div>
              <div>
                <NumberInput
                  label="Points"
                  name="points"
                  value={formData.points}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("points")}
                  maxValue={11}
                />
              </div>
            </div>
          </div>

          {/* Miscellaneous Info */}
          <div className="mt-2">
            <h2 className="text-lg font-semibold mb-4 text-[#5D6871]">
              Miscellaneous Info
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <RadioInput
                  label="ID pass"
                  name="status"
                  value={formData.status}
                  checked={formData.status}
                  onChange={handleChange}
                  previousValue={getPreviousValue("status")}
                />
              </div>
              <div>
                <TextInput
                  label="ID notes"
                  name="idNotes"
                  value={formData.idNotes}
                  onChange={handleChange}
                  previousValue={getPreviousValue("idNotes")}
                />
              </div>
              <div>
                <RadioInput
                  label="Welcome letter"
                  name="welcomeLetter"
                  value={formData.welcomeLetter}
                  checked={formData.welcomeLetter}
                  onChange={handleChange}
                  previousValue={getPreviousValue("welcomeLetter")}
                />
              </div>
              <div>
                <RadioInput
                  label="Press release?"
                  name="pressRelease"
                  value={formData.pressRelease}
                  checked={formData.pressRelease}
                  onChange={handleChange}
                  previousValue={getPreviousValue("pressRelease")}
                />
              </div>
              <div>
                <RadioInput
                  label="LE figures ready"
                  name="leFiguresReady"
                  value={formData.leFiguresReady}
                  checked={formData.leFiguresReady}
                  onChange={handleChange}
                  previousValue={getPreviousValue("leFiguresReady")}
                />
              </div>
              <div>
                <RadioInput
                  label="CE/QC review complete"
                  name="ceQcReviewComplete"
                  value={formData.ceQcReviewComplete}
                  checked={formData.ceQcReviewComplete}
                  onChange={handleChange}
                  previousValue={getPreviousValue("ceQcReviewComplete")}
                />
              </div>
              <div>
                <RadioInput
                  label="Saved to tipsheet folder"
                  name="savedToTipsheetFolder"
                  value={formData.savedToTipsheetFolder}
                  checked={formData.savedToTipsheetFolder}
                  onChange={handleChange}
                  previousValue={getPreviousValue("savedToTipsheetFolder")}
                />
              </div>
              <div>
                <RadioInput
                  label="PTS record check"
                  name="ptsRecordCheck"
                  value={formData.ptsRecordCheck}
                  checked={formData.ptsRecordCheck}
                  onChange={handleChange}
                  previousValue={getPreviousValue("ptsRecordCheck")}
                />
              </div>
              <div>
                <RadioInput
                  label="Sent pub date to author"
                  name="sentPubDateToAuthor"
                  value={formData.sentPubDateToAuthor}
                  checked={formData.sentPubDateToAuthor}
                  onChange={handleChange}
                  previousValue={getPreviousValue("sentPubDateToAuthor")}
                />
              </div>
              <div>
                <RadioInput
                  label="Sent final email to author"
                  name="sentFinalEmailToAuthor"
                  value={formData.sentFinalEmailToAuthor}
                  checked={formData.sentFinalEmailToAuthor}
                  onChange={handleChange}
                  previousValue={getPreviousValue("sentFinalEmailToAuthor")}
                />
              </div>
              <div>
                <TextInput
                  label="Consortia"
                  name="consortia"
                  value={formData.consortia}
                  onChange={handleChange}
                  previousValue={getPreviousValue("consortia")}
                />
              </div>
              <div>
                <RadioInput
                  label="Fast-track"
                  name="fastTrack"
                  value={formData.fastTrack}
                  checked={formData.fastTrack}
                  onChange={handleChange}
                  previousValue={getPreviousValue("fastTrack")}
                />
              </div>
              <div>
                <RadioInput
                  label="Free featured online"
                  name="freeFeaturedOnline"
                  value={formData.freeFeaturedOnline}
                  checked={formData.freeFeaturedOnline}
                  onChange={handleChange}
                  previousValue={getPreviousValue("freeFeaturedOnline")}
                />
              </div>
              <NewNativeSelectInput
                label="Cover sub received"
                name="coverSubReceived"
                onChange={(e) => {
                  if (e.target.value === "Selected") {
                    e.target.value = null;
                  } else if (e.target.value === "Yes") {
                    e.target.value = true;
                  } else if (e.target.value === "No") {
                    e.target.value = false;
                  }
                  handleChange(e);
                }}
                value={
                  formData.coverSubReceived
                    ? "Yes"
                    : formData.coverSubReceived === false
                    ? "No"
                    : "Selected"
                }
                placeholder="Selected"
                options={["Selected", "Yes", "No"]}
              />
              <div>
                <NumberInput
                  label="TOC order position"
                  name="tocOrderPosition"
                  value={formData.tocOrderPosition}
                  onChange={handleChange}
                  previousValue={getPreviousValue("tocOrderPosition")}
                />
              </div>
              <div>
                <TextInput
                  label="OA status"
                  name="oaStatus"
                  value={formData.oaStatus}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("oaStatus")}
                />
              </div>
            </div>
          </div>

          {/* Milestones */}
          <div className="mt-2">
            <h2 className="text-lg font-semibold mb-4 text-[#5D6871]">
              Milestones
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <DateInput
                  label="Login complete"
                  name="loginComplete"
                  value={formData.loginComplete}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("loginComplete")}
                />
              </div>
              <div>
                <DateInput
                  label="Date back from SCE"
                  name="dateBackFromSce"
                  value={formData.dateBackFromSce}
                  onChange={handleChange}
                  previousValue={getPreviousValue("dateBackFromSce")}
                />
              </div>
              <div>
                <RadioInput
                  label="SCE returned status"
                  name="sceReturnedStatus"
                  value={formData.sceReturnedStatus}
                  checked={formData.sceReturnedStatus}
                  onChange={handleChange}
                  previousValue={getPreviousValue("sceReturnedStatus")}
                />
              </div>
              <div>
                <DateInput
                  label="Assigned date"
                  name="assignedDate"
                  value={formData.assignedDate}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("assignedDate")}
                />
              </div>
              <div>
                <DateInput
                  label="Copy edit task complete"
                  name="copyEditTaskComplete"
                  value={formData.copyEditTaskComplete}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("copyEditTaskComplete")}
                />
              </div>
              <div>
                <DateInput
                  label="Proofs to author"
                  name="proofsToAuthor"
                  value={formData.proofsToAuthor}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("proofsToAuthor")}
                />
              </div>
              <div>
                <DateInput
                  label="AU proof correx returned"
                  name="auProofCorrectionsSubmitted"
                  value={formData.auProofCorrectionsSubmitted}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue(
                    "auProofCorrectionsSubmitted"
                  )}
                />
              </div>
              <div>
                <RadioInput
                  label="Revised proof requested"
                  name="revisedProofRequested"
                  value={formData.revisedProofRequested}
                  checked={formData.revisedProofRequested}
                  onChange={handleChange}
                  previousValue={getPreviousValue("revisedProofRequested")}
                />
              </div>
              <div>
                <DateInput
                  label="CFC task complete"
                  name="cfcTaskComplete"
                  value={formData.cfcTaskComplete}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("cfcTaskComplete")}
                />
              </div>
              <div>
                <DateInput
                  label="AITI task completed"
                  name="aitiTaskCompleted"
                  value={formData.aitiTaskCompleted}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("aitiTaskCompleted")}
                />
              </div>
              <div>
                <DateInput
                  label="Revised proof approved"
                  name="revisedProofApproved"
                  value={formData.revisedProofApproved}
                  onChange={handleChange}
                  previousValue={getPreviousValue("revisedProofApproved")}
                />
              </div>
            </div>
          </div>

          {/* Events */}
          <div className="mt-2">
            <h2 className="text-lg font-semibold mb-4 text-[#5D6871]">
              Events
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pb-4">
              <div>
                <DateInput
                  label="Received"
                  name="receivedDate"
                  value={formData.receivedDate}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("receivedDate")}
                />
              </div>
              <div>
                <DateInput
                  label="Revised"
                  name="revisedDate"
                  value={formData.revisedDate}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("revisedDate")}
                />
              </div>
              <div>
                <DateInput
                  label="Pre-accept"
                  name="preAcceptDate"
                  value={formData.preAcceptDate}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("preAcceptDate")}
                />
              </div>
              <div>
                <DateInput
                  label="Accepted"
                  name="acceptedDate"
                  value={formData.acceptedDate}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("acceptedDate")}
                />
              </div>
              <div>
                <TextInput
                  label="PTS milestone"
                  name="ptsMilestone"
                  value={formData.ptsMilestone}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("ptsMilestone")}
                />
              </div>
              <div>
                <DateInput
                  label="EW imported S5"
                  name="ewImportedS5"
                  value={formData.ewImportedS5}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("ewImportedS5")}
                />
              </div>
              <div>
                <DateInput
                  label="SD published on the web S5"
                  name="sdPublishedOnWebS5"
                  value={formData.sdPublishedOnWebS5}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("sdPublishedOnWebS5")}
                />
              </div>
              <div>
                <DateInput
                  label="EW imported S200"
                  name="ewImportedS200"
                  value={formData.ewImportedS200}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("ewImportedS200")}
                />
              </div>
              <div>
                <DateInput
                  label="SD published on the web S200"
                  name="sdPublishedOnWebS200"
                  value={formData.sdPublishedOnWebS200}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("sdPublishedOnWebS200")}
                />
              </div>
              <div>
                <DateInput
                  label="SD published on the web S250"
                  name="sdPublishedOnWebS250"
                  value={formData.sdPublishedOnWebS250}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("sdPublishedOnWebS250")}
                />
              </div>
              <div>
                <DateInput
                  label="SD published on the web S300"
                  name="sdPublishedOnWebS300"
                  value={formData.sdPublishedOnWebS300}
                  onChange={handleChange}
                  disabled={true}
                  previousValue={getPreviousValue("sdPublishedOnWebS300")}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 z-10 bg-white py-3 px-6 border-t border-gray-200">
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            className="text-gray-700 px-4 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className={`px-4 py-1.5 text-sm rounded ${
              hasUnsavedChanges && !formSubmitting
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
            disabled={!hasUnsavedChanges || formSubmitting}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default ArticleForm;
